import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import * as Util from '../../../common/utilities/';
import { withFlags } from '../../../flags/Flags';
import { withSession } from '../../../i18n/containers/AppInit.js';
import * as StringUtils from '../../../utilities/stringUtils';
import { NoDataScreen } from '..';

import { AssetClassEditPane, AssetClassList } from '../';

import './AssetClassOverview.scss';
import {
  Button,
  Card,
  MediaObject,
  Spinner,
  Toast,
  ToastContainer
} from '@salesforce/design-system-react';
import { ArchiveFallbackModal } from '../';
import { HeaderDropdown } from '../../../common/components/';

class AssetClassOverview extends Component {
  constructor(props) {
    super(props);
    this.state = {
      editPane: {
        isOpen: false,
        assetClass: undefined
      },
      archiveFilter: 'unarchived'
    };
  }
  componentDidMount() {
    this.props.actions.getAssetTypes();
  }

  header(count = 0) {
    const { formatMessage } = this.props.intl;
    return (
      <div className="asset-class-header">
        <HeaderDropdown
          options={[
            {
              label: formatMessage({ id: 'components.ecs.classes.header' }),
              value: 'unarchived'
            },
            {
              label: formatMessage({ id: 'components.ecs.classes.archived' }),
              value: 'archived'
            }
          ]}
          value={this.state.archiveFilter}
          onSelect={option => this.setState({ archiveFilter: option.value })}
        />
        <FormattedMessage
          id="components.ecs.classes.items"
          values={{ count }}
        />
      </div>
    );
  }

  handleListAction(session) {
    return (action, assetClass, disabled) => {
      switch (action) {
        case 'toggleExpand':
          if (assetClass?.uuid) {
            const currentExpandedClasses =
              this.props.assets.data.browsedFilter?.expandedClasses || [];
            let toggledClasses;
            if (currentExpandedClasses.includes(assetClass.uuid)) {
              toggledClasses = currentExpandedClasses.filter(
                uuid => assetClass.uuid !== uuid
              );
            } else {
              toggledClasses = [...currentExpandedClasses, assetClass.uuid];
            }
            this.props.actions.updateAssetFilter({
              expandedClasses: toggledClasses
            });
          }
          break;
        case 'edit':
          this.openPane(assetClass);
          break;
        case 'delete':
          if (disabled) {
            break;
          }
          this.props.actions.deleteAssetClass(assetClass, {
            cToken: session.cToken
          });
          break;
        case 'archive':
          if (disabled) {
            break;
          }
          if (assetClass.count > 0) {
            this.props.actions.openModal('archiveFallback', {
              action: 'assetClass',
              assetClass: assetClass
            });
          } else {
            this.props.actions.archiveAssetClass(assetClass, {
              cToken: session.cToken
            });
          }
          break;
        case 'unarchive':
          this.props.actions.unarchiveAssetClass(assetClass, {
            cToken: session.cToken
          });
          break;
        default:
          break;
      }
    };
  }

  handleAssetClassChange(updates) {
    const { assetClass } = this.state.editPane;
    const updatedAssetClass = { ...assetClass };
    Object.entries(updates).forEach(
      entry => (updatedAssetClass[entry[0]] = entry[1])
    );
    this.setState({
      editPane: {
        isOpen: true,
        assetClass: updatedAssetClass
      }
    });
  }

  openPane(classToEdit = {}) {
    const assetClass = { ...classToEdit };
    if (assetClass?.parent_class_id) {
      assetClass.isChild = true;
      const types = this.props.assets.data?.types || [];
      const parent = types.find(
        type => type.uuid === assetClass.parent_class_id
      );
      assetClass.parentSelection = [
        { id: parent.uuid, value: parent.value, label: parent.name }
      ];
    }
    this.setState({
      editPane: {
        isOpen: true,
        assetClass
      }
    });
  }

  closePane() {
    this.setState({
      editPane: {
        isOpen: false,
        assetClass: undefined
      }
    });
  }

  saveAssetClass(session) {
    return () => {
      const { assetClass } = this.state.editPane;
      const action = assetClass.uuid
        ? this.props.actions.editAssetClass
        : this.props.actions.createAssetClass;
      action(this.state.editPane.assetClass, { cToken: session.cToken });
    };
  }

  componentDidUpdate(prevProps) {
    if (Util.propChanged(prevProps, this.props, 'assets.isSaving', false)) {
      if (!this.props.assets.data.lastSave?.error) {
        this.props.actions.getAssetTypes();
        this.closePane();
        const { lastSave } = this.props.assets.data;
        if (lastSave) {
          this.props.actions.openToast('assetClass', {
            name: lastSave.assetClass.name,
            mode: lastSave.mode
          });
          if (lastSave.mode === 'archive') {
            this.props.actions.closeModal('archiveFallback');
          }
        }
      }
    }
  }

  toast() {
    const { formatMessage } = this.props.intl;
    if (this.props.toast.assetClass) {
      const heading = formatMessage(
        {
          id: `components.ecs.classes.toast.${this.props.toast.assetClass.mode}`
        },
        { assetClass: this.props.toast.assetClass.name }
      );
      return (
        <Toast
          labels={{ heading }}
          variant="success"
          duration={10000}
          onRequestClose={() => this.props.actions.closeToast('assetClass')}
        />
      );
    }

    return null;
  }

  filterClasses(types) {
    const expanded =
      this.props.assets.data?.browsedFilter?.expandedClasses || [];

    const parents = types
      .filter(type => !type.parent_class_id)
      .sort(StringUtils.sortByField('name', this.props.intl.locale));
    const children = types.filter(type => !!type.parent_class_id);
    const childrenMap = {};
    children.forEach(child => {
      const arr = childrenMap[child.parent_class_id];
      if (arr) {
        arr.push(child);
      } else {
        childrenMap[child.parent_class_id] = [child];
      }
    });

    const filtered = [];
    parents.forEach(parent => {
      filtered.push(parent);
      if (expanded.includes(parent.uuid) && childrenMap[parent.uuid]?.length) {
        childrenMap[parent.uuid]
          .sort(StringUtils.sortByField('name', this.props.intl.locale))
          .forEach(child => {
            filtered.push(child);
          });
      }
    });
    return filtered;
  }

  createAllowed(flags) {
    return !(flags && flags.isReadonly);
  }

  drawSpinner(isLoading) {
    return isLoading ? (
      <div className="pi-common-spinner">
        <Spinner variant="brand" />
      </div>
    ) : null;
  }

  renderNoData(flags) {
    const { formatMessage } = this.props.intl;

    return (
      <Card
        className="asset-class-card"
        header={<MediaObject body={this.header(0)} />}
        headerActions={
          <Button
            id="new-asset-class"
            label={formatMessage({
              id: 'components.ecs.classes.newClassButton'
            })}
            variant="brand"
            onClick={() => this.openPane()}
            disabled={!this.createAllowed(flags)}
          />
        }
      >
        <NoDataScreen
          screen="assetClasses"
          helpLink="https://help.salesforce.com/articleView?id=mc_pb_create_and_upload_asset_catalog_file.htm&type=5"
        />
      </Card>
    );
  }

  renderTable(filtered, types, showExpandColumn, flags, session) {
    const { formatMessage } = this.props.intl;
    const showWeatherColumn = this.props.rules?.settings?.weather;
    return (
      <Card
        className="asset-class-card"
        header={<MediaObject body={this.header(filtered?.length)} />}
        headerActions={
          <Button
            id="new-asset-class"
            label={formatMessage({
              id: 'components.ecs.classes.newClassButton'
            })}
            variant="brand"
            onClick={() => this.openPane()}
            disabled={!this.createAllowed(flags)}
          />
        }
      >
        <div className="asset-class-list-div">
          <AssetClassList
            types={filtered}
            unfilteredTypes={types}
            actions={this.props.actions}
            onListAction={this.handleListAction(session)}
            showExpandColumn={showExpandColumn}
            expandedRows={this.props.assets.data.browsedFilter?.expandedClasses}
            showWeatherColumn={showWeatherColumn}
          />
        </div>
      </Card>
    );
  }

  render() {
    const { isLoading } = this.props.assets?.isLoading;
    const unarchived = this.props.assets?.data?.types || [];
    const archived = this.props.assets?.data?.archivedTypes || [];
    const types =
      this.state.archiveFilter === 'unarchived' ? unarchived : archived;
    const canRender = !!types;

    return withFlags(flags => {
      return withSession(session => {
        const showExpandColumn =
          flags.parentChild &&
          !!types.filter(type => !!type.parent_class_id).length;
        const filtered = this.filterClasses(types);
        return (
          <React.Fragment>
            {this.drawSpinner(isLoading)}
            {canRender &&
            !types.length &&
            this.state.archiveFilter === 'unarchived'
              ? this.renderNoData(flags)
              : this.renderTable(
                  filtered,
                  types,
                  showExpandColumn,
                  flags,
                  session
                )}
            <AssetClassEditPane
              assetClass={this.state.editPane.assetClass}
              rules={this.props.rules}
              types={this.props.assets?.data?.types}
              isOpen={this.state.editPane.isOpen}
              onChange={this.handleAssetClassChange.bind(this)}
              onClose={this.closePane.bind(this)}
              onSave={this.saveAssetClass(session)}
            />
            <ArchiveFallbackModal
              actions={this.props.actions}
              modals={this.props.modals}
              session={session}
            />
            <ToastContainer>{this.toast()}</ToastContainer>
          </React.Fragment>
        );
      });
    });
  }
}

AssetClassOverview.propTypes = {
  assets: PropTypes.object.isRequired,
  rules: PropTypes.object.isRequired,
  modals: PropTypes.object,
  toast: PropTypes.object,
  actions: PropTypes.object,
  location: PropTypes.object
};

export default injectIntl(AssetClassOverview);
